import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type { UseQueryResult, UseMutationResult } from '@tanstack/react-query';
import { useSelector, useDispatch } from 'react-redux';

import {
  getCollection,
  find as apiFindRecord,
  createOrUpdate as apiCreateOrUpdate,
  deleteRecord as apiDeleteRecord,
} from 'api/market-view-api';
import type { GetCollectionOptions } from 'api/market-view-api';
import type {
  FilterValue,
  SortDirection,
} from 'helpers/api/build-query-params';

import { transformRecordFromServer } from 'helpers/server-data-transformers';

import { actions as currentUserActions } from 'state/current-user/current-user.slice';
import type { CurrentUserData } from 'state/current-user/current-user.slice';

import Notification from 'components/partials/notification/notification.helper';

import { findClient } from 'hooks/api/use-clients';

import type { RootState } from 'state/store';

export interface UserRecord {
  id: string | number;
  status?: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  authority?: string;
  fdUser?: boolean;
  viewPortalAsUser?: boolean;
  email?: string;
  superAdmin?: boolean;
  client?: {
    id?: string | number;
    name?: string;
    status?: string;
    [key: string]: unknown;
  } | null;
  [key: string]: unknown;
}

interface PaginationInfo {
  page: number;
  pageSize: number;
  pageCount: number;
  totalRecords: number;
}

interface CollectionLoadResponse {
  data: UserRecord[];
  pagination: PaginationInfo;
}

export const usersKeys = {
  all: ['users'] as const,
  lists: () => [...usersKeys.all, 'list'] as const,
  list: (params: Record<string, unknown>) =>
    [...usersKeys.lists(), params] as const,
  details: () => [...usersKeys.all, 'detail'] as const,
  detail: (id: string | number) => [...usersKeys.details(), id] as const,
};

const transformUser = (data: unknown): UserRecord =>
  transformRecordFromServer('user', data) as UserRecord;

// ─── List query (paginated, used by users-admin-page) ─────────────────────

export interface UseUsersListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
  searchTerms?: string;
  enabled?: boolean;
}

export interface UseUsersListResult {
  data: UserRecord[];
  pagination: PaginationInfo | null;
  loading: boolean;
  isFetching: boolean;
  error: unknown;
  refetch: () => Promise<unknown>;
}

const EMPTY_USERS: UserRecord[] = [];

export const useUsersList = (
  params: UseUsersListParams = {}
): UseUsersListResult => {
  const {
    pageSize = 10,
    pageIndex = 0,
    sortBy = [],
    filters = {},
    searchTerms = '',
    enabled = true,
  } = params;

  const queryKey = usersKeys.list({
    pageSize,
    pageIndex,
    sortBy,
    filters,
    searchTerms,
  });

  const query: UseQueryResult<CollectionLoadResponse, Error> = useQuery({
    queryKey,
    queryFn: async () => {
      const options: GetCollectionOptions = {
        pageSize,
        pageIndex,
        sortBy,
        filters,
        searchTerms,
        forceSearch: false,
      };

      const response = await getCollection('users', options);
      const records = response.data as unknown[];

      const transformed = Array.isArray(records)
        ? records.map(transformUser)
        : [];

      return { data: transformed, pagination: response.pagination };
    },
    enabled,
    placeholderData: keepPreviousData,
  });

  return {
    data: query.data?.data ?? EMPTY_USERS,
    pagination: query.data?.pagination ?? null,
    loading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
  };
};

// ─── Single user query (replaces users.find) ─────────────────────────────────

export interface UseUserResult {
  data: UserRecord | null;
  loading: boolean;
  error: unknown;
}

export const useUser = (
  userId: string | number | null | undefined
): UseUserResult => {
  const query: UseQueryResult<UserRecord, Error> = useQuery({
    queryKey: usersKeys.detail(userId ?? ''),
    queryFn: async () => {
      const response = await apiFindRecord('users', userId!);

      return transformUser(response.data);
    },
    enabled: !!userId,
  });

  return {
    data: query.data ?? null,
    loading: query.isLoading,
    error: query.error,
  };
};

// ─── Imperative find (for impersonation-banner, user-page) ───────────────────

export const findUser = async (
  id: string | number
): Promise<{ data: UserRecord }> => {
  const response = await apiFindRecord('users', id);

  return { data: transformUser(response.data) };
};

// ─── findAndAddClientData (replaces useUsers().findAndAddClientData) ──────────

export const findAndAddClientData = async (
  userId: string | number
): Promise<{ data: UserRecord }> => {
  const userResponse = await apiFindRecord('users', userId);
  const user = transformUser(userResponse.data);

  if (user.client?.id) {
    try {
      const clientResponse = await findClient(user.client.id);

      return {
        data: {
          ...user,
          client: { ...clientResponse.data },
        },
      };
    } catch {
      return { data: user };
    }
  }

  return { data: user };
};

// ─── Mutations ──────────────────────────────────────────────────────────────

export const useCreateOrUpdateUser = (): UseMutationResult<
  { data: UserRecord },
  Error,
  Record<string, unknown>
> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const currentUserId = useSelector(
    (state: RootState) =>
      (state.currentUser as { id: string | number | null })?.id
  );

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiCreateOrUpdate('users', data);

      return { data: transformUser(response.data) };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });

      if (result.data.id === currentUserId) {
        syncCurrentUser(result.data, dispatch);
      }
    },
  });
};

export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string | number; name?: string }>({
    mutationFn: async ({ id }) => {
      await apiDeleteRecord('users', id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });
    },
  });
};

export const useLockUser = (): UseMutationResult<
  { data: UserRecord },
  Error,
  UserRecord
> => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const currentUserId = useSelector(
    (state: RootState) =>
      (state.currentUser as { id: string | number | null })?.id
  );

  return useMutation({
    mutationFn: async (user) => {
      const isLocked = user.status === 'LOCKED';
      const updated = {
        ...user,
        status: isLocked ? 'ACTIVE' : 'LOCKED',
      };
      const response = await apiCreateOrUpdate('users', updated);

      return { data: transformUser(response.data) };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: usersKeys.all });

      const changeMsg = result.data.status === 'LOCKED' ? 'locked' : 'unlocked';
      const changeColor = result.data.status === 'LOCKED' ? 'info' : 'success';

      Notification.flash(
        `User ${changeMsg}`,
        changeColor,
        `You've successfully ${changeMsg} this user`
      );

      if (result.data.id === currentUserId) {
        syncCurrentUser(result.data, dispatch);
      }
    },
  });
};

// ─── Bridge: sync currentUser Redux state when the user being updated is the
// current user. Replaces the addUser extraReducer in currentUser.slice.ts. ────

function syncCurrentUser(
  userData: UserRecord,
  dispatch: ReturnType<typeof useDispatch>
): void {
  dispatch(
    currentUserActions.setCurrentUser(userData as unknown as CurrentUserData)
  );
}
